import CssBaseline from '@mui/material/CssBaseline';
import { Theme, ThemeProvider } from '@mui/material/styles';
import { useMemo } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { useBranding } from './contexts/BrandingContext';
import { createAppTheme } from './theme';

// Pages
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { Layout } from './components/layout/Layout';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { About } from './components/pages/About/About';
import { Activate } from './components/pages/Activate/activate';
import { Dashboard } from './components/pages/Dashboard/Dashboard';
import { AppointmentBooking } from './components/pages/AppointmentBookingForm/appointmentBookingForm';
import { MyAppointments } from './components/pages/MyAppointments/MyAppointments';
import { MyCalendar } from './components/pages/MyCalendar/MyCalendar';
import { MySchedule } from './components/pages/MySchedule/MySchedule';
import { ChangePassowrd } from './components/pages/ForgotPassword/changePassword';
import { ForgotPassword } from './components/pages/ForgotPassword/forgotPassword';
import { LandingPage } from './components/pages/LandingPage/LandingPage';
import { PublicBooking } from './components/pages/PublicBooking/PublicBooking';
import { publicBookingLink } from './components/pages/PublicBooking/paths';
import { Profile } from './components/pages/Profile/Profile';
import { RegisterConfirmation } from './components/pages/Register/registerConfirmation';
import { TermsAndConditions } from './components/pages/TermsAndConditions/termsAndConditions';
import { ClinicAdmin } from './components/pages/ClinicAdmin/ClinicAdmin';
import { ClinicDashboard } from './components/pages/ClinicDashboard/ClinicDashboard';
import { Clinics } from './components/pages/Clinics/Clinics';
import { PatientDetail } from './components/pages/PatientDetail/PatientDetail';
import { Patients } from './components/pages/Patients/Patients';
import { TherapistIncome } from './components/pages/TherapistIncome/TherapistIncome';
import { Evaluation } from './components/pages/Evaluation/Evaluation';
import { adminRoles, bookAppointmentLink, careRoles, clinicAdminLink, clinicDashboardLink, clinicsLink, customerRoles, forgotPasswordLink, loginLink, myAppointmentsLink, myCalendarLink, myScheduleLink, newEvaluationLink, patientFileRoles, patientsLink, registerConfirmationLink, registerLink, selfRegistrationEnabled, superAdminRoles, therapistIncomeLink, therapistRoles } from './library/constants';
import { LanguagePreference } from './library/useLanguagePreference';
import { OurServices } from './components/pages/OurServices/ourServices';

function App() {
    // Rebuilt only when the clinic's colours actually change. MUI parses these values, so
    // the theme cannot reference the CSS variables the styles files use -- it takes the same
    // two colours through the derived tokens instead. Everything driven by var(--ch-*)
    // repaints without this, so the render is only for MUI's own components.
    const { tokens } = useBranding();
    const theme: Theme = useMemo(
        () => createAppTheme(tokens.primary.main, tokens.secondary.main),
        [tokens.primary.main, tokens.secondary.main],
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {/* Renders nothing. Decides which language the shell is in -- the signed-in
                person's own, else one already picked in this browser, else the clinic whose
                address this is. See useLanguagePreference for why it is one hook. */}
            <LanguagePreference />
            <Router>
                {/* Renders nothing. Opens each newly visited page at its top -- see ScrollToTop. */}
                <ScrollToTop />
                <Layout>
                    <Routes>
                        <Route path="/" element={<Navigate to="/home" replace />} />

                        <Route path="/activate/:key" element={<Activate />} />
                        <Route path="/change-password/:key" element={<ChangePassowrd />} />

                        {/* Public Routes */}
                        <Route path="/home" element={<LandingPage />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/login" element={<LoginForm />} />
                        {/* Hidden, not gone -- see selfRegistrationEnabled. Off, a typed or
                            bookmarked address lands on sign-in rather than on a form that
                            can only fail. */}
                        <Route path={registerLink} element={selfRegistrationEnabled ? <RegisterForm /> : <Navigate to={loginLink} replace />} />
                        <Route path={registerConfirmationLink} element={selfRegistrationEnabled ? <RegisterConfirmation /> : <Navigate to={loginLink} replace />} />
                        <Route path={forgotPasswordLink} element={<ForgotPassword />} />
                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                        <Route path="/ourServices" element={<OurServices />} />
                        {/* Online booking without an account, on a clinic's own address. Owns
                            everything under it -- see PublicBooking for how to remove it. */}
                        <Route path={`${publicBookingLink}/*`} element={<PublicBooking />} />

                        {/* Protected Routes */}
                        <Route path="/dashboard" element={<ProtectedRoute allowedRoles={customerRoles}> <Dashboard /> </ProtectedRoute>} />
                        <Route path={bookAppointmentLink} element={<ProtectedRoute allowedRoles={customerRoles}> <AppointmentBooking /> </ProtectedRoute>} />
                        <Route path={myCalendarLink} element={<ProtectedRoute allowedRoles={therapistRoles}> <MyCalendar /> </ProtectedRoute>} />
                        {/* The page was '/my-schedule' until it was renamed for what it
                            actually draws. Kept as a redirect rather than dropped: a
                            therapist opens this page every morning and some of them have
                            it bookmarked. */}
                        <Route path="/my-schedule" element={<Navigate to={myCalendarLink} replace />} />
                        {/* The therapist's own working week -- when patients can book them,
                            inside the clinic's own hours. Not on '/my-schedule', which the
                            line above owes to the people who bookmarked the diary under
                            that name; see myScheduleLink. */}
                        <Route path={myScheduleLink} element={<ProtectedRoute allowedRoles={therapistRoles}> <MySchedule /> </ProtectedRoute>} />
                        <Route path={myAppointmentsLink} element={<ProtectedRoute allowedRoles={careRoles}> <MyAppointments /> </ProtectedRoute>} />
                        <Route path={patientsLink} element={<ProtectedRoute allowedRoles={therapistRoles}> <Patients /> </ProtectedRoute>} />
                        <Route path={newEvaluationLink} element={<ProtectedRoute allowedRoles={therapistRoles}> <Evaluation /> </ProtectedRoute>} />
                        {/* Below the static '/patients/new-evaluation' above it: React Router ranks a
                            static segment higher than a dynamic one either way, but reading them in
                            this order is what makes that obvious. */}
                        <Route path="/patients/:patientId" element={<ProtectedRoute allowedRoles={patientFileRoles}> <PatientDetail /> </ProtectedRoute>} />
                        {/* The same wizard again, for a patient who is already on the list.
                            A deeper path than the two above, so nothing here is ambiguous --
                            the id it carries is what turns a blank form into a follow-up. */}
                        <Route path="/patients/:patientId/new-evaluation" element={<ProtectedRoute allowedRoles={therapistRoles}> <Evaluation /> </ProtectedRoute>} />
                        <Route path={therapistIncomeLink} element={<ProtectedRoute allowedRoles={therapistRoles}> <TherapistIncome /> </ProtectedRoute>} />
                        <Route path="/profile" element={<ProtectedRoute> <Profile /> </ProtectedRoute>} />
                        <Route path={clinicsLink} element={<ProtectedRoute allowedRoles={superAdminRoles}> <Clinics /> </ProtectedRoute>} />
                        <Route path={clinicDashboardLink} element={<ProtectedRoute allowedRoles={adminRoles}> <ClinicDashboard /> </ProtectedRoute>} />
                        <Route path={clinicAdminLink} element={<ProtectedRoute allowedRoles={adminRoles}> <ClinicAdmin /> </ProtectedRoute>} />

                        {/* Catch all route
                        <Route path="*" element={<Navigate to="/home" replace />} /> */}
                    </Routes>
                </Layout>
            </Router>
        </ThemeProvider>
    );
}

export default App;